/// <amd-module name="Repos/Artifact/Discussion/Components/ArtifactDiscussion" />
import { translateDisplayNamesToStorageKeys } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { useStorageKeysInMentions } from "Mention/Common/Util";
import * as React from "react";
import { CommentThreadStatus } from "Repos/Common/Contracts/Git";
import { DiscussionStatus, IDiscussionThreadModel } from "Repos/Discussion/Common/DiscussionContracts";
import { DiscussionThread } from "Repos/Discussion/DiscussionThread";
import { IDiscussionThreadProps } from "Repos/Discussion/DiscussionThread.Types";
import { VssComponent } from "VSS/Platform/Layout";
import { IArtifactDiscussionThreadModel } from "../ArtifactDiscussionContracts";
import { getArtifactDiscussionUIService, IArtifactDiscussionContext } from "../ArtifactDiscussionUIService";

export interface IArtifactDiscussionProps
    extends Pick<
        IDiscussionThreadProps,
        "author" | "className" | "commentEditorClassName" | "getMarkdownEditorProps" | "permissions" | "personaProvider"
    > {
    /**
     * Uri of the artifact (commit, changeset, shelvest, etc.)
     */
    artifactUri: string;

    /**
     * Called when all the comments in a thread are deleted.
     */
    onDeleteThread?: (threadId: number) => void;

    /**
     * Called when a newly started discussion thread is cancelled.
     */
    onCancelThread?: (threadId: number) => void;

    /**
     * Called when the status of the thread changes.
     */
    onChangeStatus?: (status: CommentThreadStatus) => void;

    /**
     * Discussion thread to display.
     */
    thread: IArtifactDiscussionThreadModel;
}

export class ArtifactDiscussion extends VssComponent<IArtifactDiscussionProps> {
    public render(): JSX.Element {
        const {
            author,
            className,
            commentEditorClassName,
            getMarkdownEditorProps,
            onCancelThread,
            permissions,
            personaProvider,
            thread
        } = this.props;

        return (
            <DiscussionThread
                author={author}
                className={className}
                commentEditorClassName={commentEditorClassName}
                getMarkdownEditorProps={getMarkdownEditorProps}
                permissions={permissions}
                personaProvider={personaProvider}
                onAddComment={this.onAddComment}
                onChangeStatus={this.onChangeStatus}
                onDismiss={onCancelThread}
                onRemoveComment={this.onRemoveComment}
                onUpdateComment={this.onUpdateComment}
                showLikes={false}
                thread={thread as IDiscussionThreadModel}
            />
        );
    }

    private onAddComment = async (commentId: number, comment: string): Promise<void> => {
        comment = await this.performTranslations(comment);
        getArtifactDiscussionUIService(this.context.pageContext).addComment(this.getDiscussionContext(), commentId, comment);
    };

    private onChangeStatus = (status: DiscussionStatus): void => {
        const { onChangeStatus } = this.props;
        getArtifactDiscussionUIService(this.context.pageContext).changeStatus(this.getDiscussionContext(), status as number, onChangeStatus);
    };

    private onRemoveComment = (commentId: number): void => {
        const { onDeleteThread } = this.props;
        getArtifactDiscussionUIService(this.context.pageContext).removeComment(this.getDiscussionContext(), commentId, onDeleteThread);
    };

    private onUpdateComment = async (commentId: number, comment: string): Promise<void> => {
        comment = await this.performTranslations(comment);
        getArtifactDiscussionUIService(this.context.pageContext).updateComment(this.getDiscussionContext(), commentId, comment);
    };

    private performTranslations = async (comment: string): Promise<string> => {
        return useStorageKeysInMentions(this.context.pageContext) ? translateDisplayNamesToStorageKeys(this.context.pageContext, comment) : comment;
    };

    private getDiscussionContext(): IArtifactDiscussionContext {
        const { artifactUri, thread } = this.props;
        return { artifactUri, thread };
    }
}
